import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils/cn';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps, QuestionOption } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

interface RankingConfig {
  // API uses 'drag_drop' | 'numbered' | 'arrows', normalize to internal values
  displayStyle?: 'drag' | 'drag_drop' | 'number' | 'numbered' | 'arrows';
  showRankNumbers?: boolean;
  instructionText?: string;
  mobileOptimized?: boolean;
  allowTies?: boolean;
  highlightChanges?: boolean;
  requireAllRanked?: boolean;
  minSelections?: number;
  maxSelections?: number;
}

type NormalizedDisplayStyle = 'drag' | 'number' | 'arrows';

function normalizeDisplayStyle(style?: string): NormalizedDisplayStyle {
  switch (style) {
    case 'drag':
    case 'drag_drop':
      return 'drag';
    case 'number':
    case 'numbered':
      return 'number';
    case 'arrows':
      return 'arrows';
    default:
      return 'drag';
  }
}

interface SortableItemProps {
  id: string;
  option: QuestionOption;
  rank: number;
  showRankNumber: boolean;
  displayStyle: NormalizedDisplayStyle;
  disabled?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onRankChange?: (rank: number) => void;
  totalItems: number;
  isAnchored?: boolean;
  isDragging?: boolean;
  isHighlighted?: boolean;
  highlightChanges?: boolean;
}

// ============================================================================
// ICONS
// ============================================================================

function GripIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 8h16M4 16h16"
      />
    </svg>
  );
}

function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ArrowDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 2a1 1 0 00-1 1v1.323l-3.954 1.582a1 1 0 00-.593 1.054l.42 2.94-2.143 4.714A1 1 0 003.615 16H8v2a1 1 0 102 0v-2h4.385a1 1 0 00.885-1.387l-2.143-4.714.42-2.94a1 1 0 00-.593-1.054L9 4.323V3a1 1 0 00-1-1z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// ============================================================================
// SORTABLE ITEM COMPONENT
// ============================================================================

function SortableItem({
  id,
  option,
  rank,
  showRankNumber,
  displayStyle,
  disabled,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onRankChange,
  totalItems,
  isAnchored,
  isDragging: isDraggingProp,
  isHighlighted,
  highlightChanges,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: disabled || isAnchored || displayStyle !== 'drag' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRank = parseInt(e.target.value);
    if (!isNaN(newRank) && newRank >= 1 && newRank <= totalItems && onRankChange) {
      onRankChange(newRank);
    }
  };

  const actuallyDragging = isDragging || isDraggingProp;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-gray-900 transition-all duration-200',
        actuallyDragging
          ? 'shadow-lg border-primary-300 dark:border-primary-600 z-10 scale-[1.02]'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
        disabled && 'opacity-50 cursor-not-allowed',
        isAnchored && 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-700',
        highlightChanges && isHighlighted && !actuallyDragging && 'ring-2 ring-primary-200 dark:ring-primary-800 bg-primary-50 dark:bg-primary-950/30'
      )}
    >
      {/* Rank number badge */}
      {showRankNumber && displayStyle !== 'number' && (
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium shrink-0 transition-colors',
            isHighlighted && highlightChanges
              ? 'bg-primary-600 text-white'
              : 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
          )}
        >
          {rank}
        </span>
      )}

      {/* Number input for number style */}
      {displayStyle === 'number' && (
        <input
          type="number"
          min={1}
          max={totalItems}
          value={rank}
          onChange={handleNumberChange}
          disabled={disabled || isAnchored}
          className={cn(
            'h-8 w-14 text-center rounded border text-sm font-medium',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100',
            disabled && 'bg-gray-100 dark:bg-gray-700'
          )}
        />
      )}

      {/* Drag handle for drag style */}
      {displayStyle === 'drag' && !isAnchored && (
        <div
          className={cn(
            'cursor-grab active:cursor-grabbing touch-none p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors',
            disabled && 'cursor-not-allowed'
          )}
          {...attributes}
          {...listeners}
        >
          <GripIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
      )}

      {/* Pinned indicator */}
      {isAnchored && (
        <PinIcon className="h-4 w-4 text-amber-500 shrink-0" />
      )}

      {/* Option content */}
      <div className="flex-1 min-w-0">
        {(option as { image?: string }).image && (
          <img
            src={(option as { image?: string }).image}
            alt={option.text}
            className="w-full h-16 object-cover rounded mb-2"
          />
        )}
        <span className="text-sm text-gray-900 dark:text-gray-100">{option.text}</span>
      </div>

      {/* Arrow buttons for arrows style */}
      {displayStyle === 'arrows' && !isAnchored && (
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={disabled || !canMoveUp}
            className={cn(
              'p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors',
              (!canMoveUp || disabled) && 'opacity-30 cursor-not-allowed'
            )}
            aria-label="Move up"
          >
            <ArrowUpIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={disabled || !canMoveDown}
            className={cn(
              'p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors',
              (!canMoveDown || disabled) && 'opacity-30 cursor-not-allowed'
            )}
            aria-label="Move down"
          >
            <ArrowDownIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function Ranking({
  question,
  value,
  onChange,
  disabled,
  errors,
  displayOrder,
}: QuestionComponentProps<string[] | null>) {
  const options: QuestionOption[] = question.config?.options || [];
  const config = question.config as RankingConfig | undefined;

  // Extract settings - normalize displayStyle from API format
  const displayStyle = normalizeDisplayStyle(config?.displayStyle);
  const showRankNumbers = config?.showRankNumbers !== false;
  const instructionText = config?.instructionText;
  const highlightChanges = config?.highlightChanges !== false;
  const mobileOptimized = config?.mobileOptimized !== false;

  // Track recently moved item for highlighting
  const [recentlyMoved, setRecentlyMoved] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  // Clear highlight after animation
  useEffect(() => {
    if (recentlyMoved) {
      const timer = setTimeout(() => setRecentlyMoved(null), 1000);
      return () => clearTimeout(timer);
    }
  }, [recentlyMoved]);

  // Identify anchored items
  const anchoredTopItems = useMemo(() =>
    options.filter(opt => opt.anchored === 'top').map(opt => opt.value),
    [options]
  );
  const anchoredBottomItems = useMemo(() =>
    options.filter(opt => opt.anchored === 'bottom').map(opt => opt.value),
    [options]
  );

  // Initialize items order
  const [items, setItems] = useState<string[]>(() => {
    if (value && value.length > 0) {
      return value;
    }
    // Apply display order or use default option order
    const orderedOptions = displayOrder
      ? displayOrder.map((i) => options[i]).filter(Boolean)
      : options;

    // Sort to respect anchored positions
    const sortedItems = orderedOptions.map(opt => opt.value);
    const anchoredTop = sortedItems.filter(v => anchoredTopItems.includes(v));
    const anchoredBottom = sortedItems.filter(v => anchoredBottomItems.includes(v));
    const middle = sortedItems.filter(
      v => !anchoredTopItems.includes(v) && !anchoredBottomItems.includes(v)
    );

    return [...anchoredTop, ...middle, ...anchoredBottom];
  });

  // Configure sensors with touch support for mobile
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isAnchored = useCallback(
    (itemValue: string) =>
      anchoredTopItems.includes(itemValue) || anchoredBottomItems.includes(itemValue),
    [anchoredTopItems, anchoredBottomItems]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragId(null);

      if (over && active.id !== over.id) {
        const activeValue = active.id as string;
        const overValue = over.id as string;

        // Don't allow dragging anchored items or dropping on them
        if (isAnchored(activeValue) || isAnchored(overValue)) {
          return;
        }

        const oldIndex = items.indexOf(activeValue);
        const newIndex = items.indexOf(overValue);

        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);
        onChange(newItems);
        setRecentlyMoved(activeValue);
      }
    },
    [items, onChange, isAnchored]
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index <= 0) return;

      // Find the previous non-anchored item
      let targetIndex = index - 1;
      while (targetIndex >= 0 && isAnchored(items[targetIndex])) {
        targetIndex--;
      }
      if (targetIndex < 0) return;

      const itemValue = items[index];
      const newItems = arrayMove(items, index, targetIndex);
      setItems(newItems);
      onChange(newItems);
      setRecentlyMoved(itemValue);
    },
    [items, onChange, isAnchored]
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= items.length - 1) return;

      // Find the next non-anchored item
      let targetIndex = index + 1;
      while (targetIndex < items.length && isAnchored(items[targetIndex])) {
        targetIndex++;
      }
      if (targetIndex >= items.length) return;

      const itemValue = items[index];
      const newItems = arrayMove(items, index, targetIndex);
      setItems(newItems);
      onChange(newItems);
      setRecentlyMoved(itemValue);
    },
    [items, onChange, isAnchored]
  );

  const handleRankChange = useCallback(
    (currentIndex: number, newRank: number) => {
      const newIndex = newRank - 1;
      if (newIndex === currentIndex) return;
      if (newIndex < 0 || newIndex >= items.length) return;

      // Don't allow moving to or from anchored positions
      if (isAnchored(items[currentIndex]) || isAnchored(items[newIndex])) {
        return;
      }

      const itemValue = items[currentIndex];
      const newItems = arrayMove(items, currentIndex, newIndex);
      setItems(newItems);
      onChange(newItems);
      setRecentlyMoved(itemValue);
    },
    [items, onChange, isAnchored]
  );

  const getOptionByValue = (optValue: string): QuestionOption | undefined => {
    return options.find((opt) => opt.value === optValue);
  };

  const canMoveUp = (index: number): boolean => {
    if (index <= 0) return false;
    // Check if there's a non-anchored item above
    for (let i = index - 1; i >= 0; i--) {
      if (!isAnchored(items[i])) return true;
    }
    return false;
  };

  const canMoveDown = (index: number): boolean => {
    if (index >= items.length - 1) return false;
    // Check if there's a non-anchored item below
    for (let i = index + 1; i < items.length; i++) {
      if (!isAnchored(items[i])) return true;
    }
    return false;
  };

  // Default instruction based on style
  const getDefaultInstruction = () => {
    switch (displayStyle) {
      case 'drag':
        return 'Drag and drop to rank items in order of preference (1 = most preferred)';
      case 'arrows':
        return 'Use the arrows to reorder items (1 = most preferred)';
      case 'number':
        return 'Enter numbers to rank items (1 = most preferred)';
      default:
        return 'Rank items in order of preference';
    }
  };

  // Calculate completion status
  const rankedCount = items.filter(v => !isAnchored(v)).length;
  const totalRankable = options.filter(opt => !opt.anchored).length;

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div className="space-y-3">
        {/* Instructions */}
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {instructionText || getDefaultInstruction()}
        </p>

        {/* Progress indicator */}
        {config?.requireAllRanked !== false && (
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <CheckIcon className={cn(
              'h-4 w-4',
              rankedCount === totalRankable ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'
            )} />
            <span>
              {rankedCount}/{totalRankable} items ranked
            </span>
          </div>
        )}

        {/* Ranking list */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className={cn(
              'space-y-2',
              mobileOptimized && 'touch-manipulation'
            )}>
              {items.map((itemValue, index) => {
                const option = getOptionByValue(itemValue);
                if (!option) return null;

                return (
                  <SortableItem
                    key={itemValue}
                    id={itemValue}
                    option={option}
                    rank={index + 1}
                    showRankNumber={showRankNumbers}
                    displayStyle={displayStyle}
                    disabled={disabled}
                    onMoveUp={() => handleMoveUp(index)}
                    onMoveDown={() => handleMoveDown(index)}
                    canMoveUp={canMoveUp(index) && !isAnchored(itemValue)}
                    canMoveDown={canMoveDown(index) && !isAnchored(itemValue)}
                    onRankChange={(newRank) => handleRankChange(index, newRank)}
                    totalItems={items.length}
                    isAnchored={isAnchored(itemValue)}
                    isDragging={activeDragId === itemValue}
                    isHighlighted={recentlyMoved === itemValue}
                    highlightChanges={highlightChanges}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

        {/* Mobile hint */}
        {mobileOptimized && displayStyle === 'drag' && (
          <p className="text-xs text-gray-400 dark:text-gray-500 sm:hidden text-center">
            Tap and hold to drag items
          </p>
        )}
      </div>
    </QuestionWrapper>
  );
}
